import React from "react";
import { resolveMedia } from "@/lib/api";
import { Trophy, Crown, CalendarBlank, Clock, MapPin } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const TeamChip = ({ info }) => {
  if (!info) return null;
  return info.logo_url
    ? <img src={resolveMedia(info.logo_url)} alt="" className="w-8 h-8 object-contain shrink-0" />
    : <span className="w-8 h-8 flex items-center justify-center text-xs font-bold shrink-0" style={{ backgroundColor: info.primary_color || "#FF5722", color: "#000" }}>{(info.name || "?").charAt(0)}</span>;
};

function MatchRow({ teamId, score, isWinner, teams, decided, placeholder }) {
  const info = teamId ? teams[teamId] : null;
  return (
    <div className={`flex items-center gap-2.5 px-4 py-2.5 ${isWinner ? "bg-[#FF5722]/15" : ""}`}>
      <span className={`font-display text-base w-6 text-center tabular-nums ${info ? "text-white/50" : "text-white/20"}`}>{info ? info.seed : ""}</span>
      <TeamChip info={info} />
      <span className={`flex-1 min-w-0 truncate text-[15px] ${isWinner ? "font-bold text-white" : info ? "text-white/85" : "text-white/30 italic"}`}>
        {info ? info.name : placeholder}
      </span>
      <span className={`font-display text-2xl tabular-nums w-10 text-right ${isWinner ? "text-[#FF5722]" : "text-white/50"}`}>
        {decided && score != null ? score : ""}
      </span>
    </div>
  );
}

function MatchCard({ match, teams, onEdit }) {
  const decided = match.status === "completed";
  const isBye = match.status === "bye";
  const clickable = !!onEdit && !isBye;
  const winA = match.winner_team_id && match.winner_team_id === match.a_team_id;
  const winB = match.winner_team_id && match.winner_team_id === match.b_team_id;
  const court = (match.court || "").trim();
  const bothSet = match.a_team_id && match.b_team_id;
  const statusLabel = isBye ? "Bye — advances" : decided ? "Final" : bothSet ? (match.date ? "Scheduled" : "Upcoming") : "Awaiting teams";
  const statusCls = isBye ? "text-[#00E676]" : decided ? "text-[#FF5722]" : bothSet ? "text-[#FFEA00]" : "text-white/25";

  return (
    <div
      data-testid={`playoff-match-${match.id}`}
      onClick={clickable ? () => onEdit(match) : undefined}
      className={`w-72 sm:w-80 border bg-[#121212] divide-y divide-white/10 transition-colors ${clickable ? "cursor-pointer border-white/15 hover:border-[#FF5722]" : "border-white/10"} ${decided ? "border-[#FF5722]/40" : ""}`}
    >
      <MatchRow teamId={match.a_team_id} score={match.a_score} isWinner={winA} teams={teams} decided={decided} placeholder={isBye ? "Bye" : "TBD"} />
      <MatchRow teamId={match.b_team_id} score={match.b_score} isWinner={winB} teams={teams} decided={decided} placeholder={isBye ? "Bye" : "TBD"} />
      <div className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`playoff-match-info-${match.id}`}>
        <span className={`text-[10px] uppercase tracking-wide font-bold ${statusCls}`}>{statusLabel}</span>
        {match.date && (
          <span className="flex items-center gap-1 text-[11px] text-white/60">
            <CalendarBlank size={12} /> {fmtDate(match.date)}
          </span>
        )}
        {match.date && fmtTime(match.date) && (
          <span className="flex items-center gap-1 text-[11px] text-white/60">
            <Clock size={12} /> {fmtTime(match.date)}
          </span>
        )}
        {court && (
          <span className="flex items-center gap-1 text-[11px] text-[#FF5722]">
            <MapPin size={12} weight="fill" /> {court}
          </span>
        )}
        {!isBye && !match.date && !court && bothSet && !decided && (
          <span className="text-[11px] text-white/30 italic">Date & court TBA</span>
        )}
        {clickable && <span className="ml-auto text-[10px] uppercase tracking-wide text-[#FF5722]/70">Manage</span>}
      </div>
    </div>
  );
}

const pairsOf = (matches) => {
  const out = [];
  for (let i = 0; i < matches.length; i += 2) out.push(matches.slice(i, i + 2));
  return out;
};

export function PlayoffBracketView({ bracket, onEditMatch }) {
  if (!bracket || !bracket.rounds) return null;
  const teams = bracket.teams || {};
  const champ = bracket.champion_team_id ? teams[bracket.champion_team_id] : null;
  const last = bracket.rounds.length - 1;

  return (
    <div data-testid="playoff-bracket">
      {champ && (
        <div data-testid="playoff-champion" className="mb-8 border border-[#FF5722] bg-gradient-to-r from-[#FF5722]/20 to-transparent px-6 py-5 flex items-center gap-4">
          <Crown size={40} weight="fill" className="text-[#FF5722] shrink-0" />
          <div className="flex items-center gap-3 min-w-0">
            {champ.logo_url
              ? <img src={resolveMedia(champ.logo_url)} alt="" className="w-12 h-12 object-contain" />
              : <span className="w-12 h-12 flex items-center justify-center font-display text-2xl" style={{ backgroundColor: champ.primary_color || "#FF5722", color: "#000" }}>{champ.name.charAt(0)}</span>}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF5722] font-bold">Champion</p>
              <p className="font-display text-3xl uppercase truncate leading-none">{champ.name}</p>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-14 min-w-max items-stretch">
          {bracket.rounds.map((rd, r) => (
            <div key={rd.round} data-testid={`playoff-round-${rd.round}`} className="flex flex-col">
              <div className="flex items-center gap-2 h-10 mb-3">
                <Trophy size={16} weight="fill" className="text-[#FF5722]" />
                <h4 className="font-display text-xl sm:text-2xl uppercase whitespace-nowrap">{rd.name}</h4>
              </div>
              <div className="flex-1 flex flex-col">
                {pairsOf(rd.matches).map((pair, pi) => (
                  <div key={pi} className="flex-1 flex flex-col justify-around relative py-2">
                    {pair.map((m) => (
                      <MatchCard key={m.id} match={m} teams={teams} onEdit={onEditMatch} />
                    ))}
                    {r < last && pair.length === 2 && (
                      <>
                        <div aria-hidden className="absolute -right-7 w-7 border-t border-b border-r border-white/25" style={{ top: "25%", bottom: "25%" }} />
                        <div aria-hidden className="absolute -right-14 w-7 border-t border-white/25" style={{ top: "50%" }} />
                      </>
                    )}
                    {r < last && pair.length === 1 && (
                      <div aria-hidden className="absolute -right-14 w-14 border-t border-white/25" style={{ top: "50%" }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
